import axios from 'axios';

import { buildApiUrl } from './apiConfig';
import { ApiEnvelope, getApiErrorMessage, unwrapApiData } from './apiResponse';

export interface SchoolRegistrationData {
  schoolName: string;
  schoolSlug: string;
  address?: string;
  contactInfo?: {
    phone?: string;
    email?: string;
    website?: string;
  };
  adminName: string;
  adminEmail: string;
  adminPassword: string;
}

export interface SchoolRegistrationResponse {
  message: string;
  schoolId: string;
  schoolSlug: string;
  adminId: string;
}

interface SchoolRegistrationApiResponse {
  message: string;
  school: {
    school_id: string;
    school_slug: string;
    name: string;
  };
  admin_user: {
    user_id: string;
    email: string;
    role: string;
  };
}

/**
 * Register a new school with an admin user
 */
export const registerSchool = async (
  data: SchoolRegistrationData,
): Promise<SchoolRegistrationResponse> => {
  try {
    const response = await axios.post<ApiEnvelope<SchoolRegistrationApiResponse>>(
      buildApiUrl('auth/register', 'v2'),
      data,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    const registration = unwrapApiData(response.data, 'Registration failed');
    return {
      message: registration.message,
      schoolId: registration.school.school_id,
      schoolSlug: registration.school.school_slug,
      adminId: registration.admin_user.user_id,
    };
  } catch (error: any) {
    if (error.request && !error.response) {
      // Request made but no response
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Registration failed'));
  }
};

/**
 * Generate a URL-friendly slug from school name
 * @param name School name
 * @returns URL-friendly slug
 */
export const generateSchoolSlug = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Check if a school slug is available
 * Note: This will be implemented when we add the check endpoint
 */
export const checkSlugAvailability = async (slug: string): Promise<boolean> => {
  // TODO: Implement API call to check slug availability
  // For now, return true (assume available)
  return true;
};
